import { cn } from '@/lib/utils';
import { UploadSolid } from '@mynaui/icons-react';
import React, { useRef, useState } from 'react';
import { FileUploadItem } from './FileList';
import { Button } from './ui/button';

interface AdvancedUploadProps {
  onFilesAdded?: (files: FileUploadItem[]) => void;
}

const AdvancedUpload: React.FC<AdvancedUploadProps> = ({ onFilesAdded }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const directoryInputRef = useRef<HTMLInputElement>(null);

  const processFiles = (files: FileList, isDirectory = false) => {
    const fileData = Array.from(files).map(
      (file): FileUploadItem => ({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        path: file.webkitRelativePath || file.name,
        size: file.size,
        type: file.type,
        file: file,
        isDirectory: isDirectory,
      }),
    );

    if (onFilesAdded) onFilesAdded(fileData);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    setIsProcessing(true);
    const items = Array.from(e.dataTransfer.items).map((item) => ({ kind: item.kind, entry: item.webkitGetAsEntry() }));
    const allFiles: FileUploadItem[] = [];

    try {
      // Process all dropped items
      for (const item of items) {
        if (item.kind === 'file' && item.entry) {
          await traverseFileTree(item.entry, allFiles);
        }
      }

      // Convert to file objects with proper paths
      const fileData = allFiles.map((fileInfo: any) => ({
        id: Math.random().toString(36).substr(2, 9),
        name: fileInfo.name,
        path: fileInfo.fullPath,
        size: fileInfo.file.size,
        type: fileInfo.file.type,
        file: fileInfo.file,
        isDirectory: true,
      }));

      // Call the callback if provided
      if (onFilesAdded) {
        onFilesAdded(fileData);
      }
    } catch (error) {
      console.error('Error processing dropped files:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const traverseFileTree = (item: any, allFiles: any[], path = ''): Promise<void> => {
    return new Promise<void>((resolve) => {
      if (item.isFile) {
        item.file((file: File) => {
          allFiles.push({
            file: file,
            name: file.name,
            fullPath: path + file.name,
          });
          resolve();
        });
      } else if (item.isDirectory) {
        const dirReader = item.createReader();
        const readEntries = () => {
          dirReader.readEntries(async (entries: any[]) => {
            if (entries.length === 0) {
              resolve();
              return;
            }

            const promises = entries.map((entry: any) => traverseFileTree(entry, allFiles, path + item.name + '/'));

            await Promise.all(promises);

            // Continue reading if there are more entries
            readEntries();
          });
        };
        readEntries();
      } else {
        resolve();
      }
    });
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'border flex flex-col items-center border-dashed rounded-lg pt-8 pb-6 text-center mb-5 relative transition-colors w-full select-none',
        isDragging
          ? 'border-blue-500 bg-blue-50 dark:bg-slate-900 dark:border-slate-800'
          : 'border-gray-300/70 bg-gray-50 dark:bg-stone-900 dark:border-stone-800',
      )}>
      <UploadSolid className='size-12 text-gray-400 dark:text-gray-600 mb-4' />
      {isProcessing && (
        <div className='absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg'>
          <div className='text-gray-700'>Processing files...</div>
        </div>
      )}

      <h3 className='text-lg font-semibold'>Drop files or directories here</h3>
      <p className='text-sm lowercase text-gray-600 mb-4'>Or use the buttons below:</p>

      <div className='flex justify-center items-center gap-2'>
        <Button variant='ghost' onClick={() => fileInputRef.current?.click()} disabled={isProcessing}>
          Select Files
        </Button>
        <span className='text-xs font-medium text-gray-500'>or</span>
        <Button variant='ghost' onClick={() => directoryInputRef.current?.click()} disabled={isProcessing}>
          Select Directory
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type='file'
        multiple
        className='hidden'
        onChange={(e) => {
          if (e.target.files) {
            processFiles(e.target.files, false);
          }
          e.target.value = '';
        }}
      />

      <input
        ref={directoryInputRef}
        type='file'
        {...({ webkitdirectory: '' } as any)}
        className='hidden'
        onChange={(e) => {
          if (e.target.files) {
            processFiles(e.target.files, true);
          }
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default AdvancedUpload;
